import React from 'react';
import { IBlock, IBlockData } from '@core/typings';
import { BasicType } from '@core/constants';
import { createBlock } from '@core/utils/createBlock';
import { getImg } from '@core/utils/getImg';
import { mergeBlock } from '@core/utils/mergeBlock';
import { t } from '@core/utils';
import { normalizeLinkHref } from '@core/utils/normalizeLinkHref';
import { BasicBlock } from '@core/components/BasicBlock';

export type ISocial = IBlockData<
	{
		align?: string;
		color?: string;
		'container-background-color'?: string;
		'border-radius'?: string;
		'icon-height'?: string;
		'icon-size'?: string;
		mode?: 'vertical' | 'horizontal';
		'icon-padding': string;
		'text-padding': string;
		'text-decoration'?: string;
		padding?: string;
		'inner-padding'?: string;
		'font-family'?: string;
		'font-size'?: string;
		'font-style'?: string;
		'font-weight'?: string;
		'line-height'?: string;
		'letter-spacing'?: string;
	},
	{
		elements: Array<{
			content: string;
			src: string;
			align?: string;
			alt?: string;
			'background-color'?: string;
			'border-radius'?: string;
			color?: string;
			'font-family'?: string;
			'font-size'?: string;
			'font-style'?: string;
			'font-weight'?: string;
			'letter-spacing'?: string;
			href?: string;
			'icon-height'?: string;
			'icon-size'?: string;
			'line-height'?: string;
			name?: string;
			padding?: string;
			'icon-padding'?: string;
			'text-padding'?: string;
			target?: string;
			title?: string;
			'text-decoration'?: string;
			'vertical-align'?: string;
		}>;
	}
>;

const DEFAULT_SOCIAL_COLOR = '#333333';
const DEFAULT_SOCIAL_ICON_SIZE = '20';
const WEBSITE_ICON_PATH =
	'M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8m7.5-6.923c-.67.204-1.335.82-1.887 1.855A8 8 0 0 0 5.145 4H7.5zM4.09 4a9.3 9.3 0 0 1 .64-1.539 7 7 0 0 1 .597-.933A7.03 7.03 0 0 0 2.255 4zm-.582 3.5c.03-.877.138-1.718.312-2.5H1.674a7 7 0 0 0-.656 2.5zM4.847 5a12.5 12.5 0 0 0-.338 2.5H7.5V5zM8.5 5v2.5h2.99a12.5 12.5 0 0 0-.337-2.5zM4.51 8.5a12.5 12.5 0 0 0 .337 2.5H7.5V8.5zm3.99 0V11h2.653c.187-.765.306-1.608.338-2.5zM5.145 12q.208.58.468 1.068c.552 1.035 1.218 1.65 1.887 1.855V12zm.182 2.472a7 7 0 0 1-.597-.933A9.3 9.3 0 0 1 4.09 12H2.255a7 7 0 0 0 3.072 2.472M3.82 11a13.7 13.7 0 0 1-.312-2.5h-2.49c.062.89.291 1.733.656 2.5zm6.853 3.472A7 7 0 0 0 13.745 12H11.91a9.3 9.3 0 0 1-.64 1.539 7 7 0 0 1-.597.933M8.5 12v2.923c.67-.204 1.335-.82 1.887-1.855q.26-.487.468-1.068zm3.68-1h2.146c.365-.767.594-1.61.656-2.5h-2.49a13.7 13.7 0 0 1-.312 2.5m2.802-3.5a7 7 0 0 0-.656-2.5H12.18c.174.782.282 1.623.312 2.5zM11.27 2.461c.247.464.462.98.64 1.539h1.835a7 7 0 0 0-3.072-2.472c.218.284.418.598.597.933M10.855 4a8 8 0 0 0-.468-1.068C9.835 1.897 9.17 1.282 8.5 1.077V4z';

function normalizeSvgSize(size?: string) {
	const match = size?.trim().match(/\d+(?:\.\d+)?/);
	return match?.[0] || DEFAULT_SOCIAL_ICON_SIZE;
}

function getWebsiteIconSrc({
	color = DEFAULT_SOCIAL_COLOR,
	size = DEFAULT_SOCIAL_ICON_SIZE,
}: {
	color?: string;
	size?: string;
} = {}) {
	const normalizedSize = normalizeSvgSize(size);
	const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${normalizedSize}" height="${normalizedSize}" fill="${color}" viewBox="0 0 16 16"><path d="${WEBSITE_ICON_PATH}"/></svg>`;
	return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

export const Social: IBlock<ISocial> = createBlock({
	get name() {
		return t('Social');
	},
	type: BasicType.SOCIAL,
	create: payload => {
		const INSTAGRAM_ICON_SRC = 'https://bay.cdn.bkat.io/smc/instagram-icon.png';
		const FACEBOOK_ICON_SRC = 'https://bay.cdn.bkat.io/smc/facebook-icon.png';
		const SOCIAL_X_ICON_SRC = 'https://bay.cdn.bkat.io/smc/twitter-logo-gray.png';
		const LINKEDIN_ICON_SRC = 'https://bay.cdn.bkat.io/smc/linkedin-icon.png';
		const YOUTUBE_ICON_SRC = 'https://cdn-icons-png.flaticon.com/128/174/174883.png';
		const TIKTOK_ICON_SRC = 'https://cdn-icons-png.flaticon.com/128/3046/3046126.png';
		const defaultData: ISocial = {
			type: BasicType.SOCIAL,
			data: {
				value: {
					elements: [
						{
							name: 'facebook',
							href: '#',
							target: '_blank',
							src: FACEBOOK_ICON_SRC,
							content: '',
						},
						{
							name: 'instagram',
							href: '#',
							target: '_blank',
							src: INSTAGRAM_ICON_SRC,
							content: '',
						},
						{
							name: 'social_x',
							href: '#',
							target: '_blank',
							src: SOCIAL_X_ICON_SRC,
							content: '',
						},
						{
							name: 'linkedin',
							href: '#',
							target: '_blank',
							src: LINKEDIN_ICON_SRC,
							content: '',
						},
						{
							name: 'youtube',
							href: '#',
							target: '_blank',
							src: YOUTUBE_ICON_SRC,
							content: '',
						},
						{
							name: 'tiktok',
							href: '#',
							target: '_blank',
							src: TIKTOK_ICON_SRC,
							content: '',
						},
					],
				},
			},
			attributes: {
				align: 'center',
				color: '#333333',
				mode: 'horizontal',
				'font-size': '14px',
				'font-weight': 'normal',
				'border-radius': '3px',
				padding: '0px 0px 0px 0px',
				'inner-padding': '4px 4px 4px 4px',
				'line-height': '22px',
				'letter-spacing': 'normal',
				'text-padding': '4px 4px 4px 0px',
				'icon-padding': '0px',
				'icon-size': '20px',
				'container-background-color': '#FFFFFF',
				'font-family': 'Arial, sans-serif',
				'text-decoration': 'none',
			},
			children: [],
		};
		return mergeBlock(defaultData, payload);
	},
	validParentType: [BasicType.COLUMN],
	render(params) {
		const { data } = params;
		const canonicalIconSrcByName: Partial<
			Record<NonNullable<ISocial['data']['value']['elements'][0]['name']>, string>
		> = {
			instagram: 'https://bay.cdn.bkat.io/smc/instagram-icon.png',
			linkedin: 'https://bay.cdn.bkat.io/smc/linkedin-icon.png',
			'linkedin-business': 'https://bay.cdn.bkat.io/smc/linkedin_business-icon.png',
		};
		const legacyIconNameBySrc: Record<string, keyof typeof canonicalIconSrcByName> = {
			'https://bay.cdn.bkat.io/www/smc/onboarding/instagram-icon.svg': 'instagram',
			'https://cdn-icons-png.flaticon.com/128/174/174857.png': 'linkedin',
			'https://cdn-icons-png.flaticon.com/128/3536/3536505.png': 'linkedin-business',
			'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2220%22%20height%3D%2220%22%20fill%3D%22none%22%20viewBox%3D%220%200%2016%2016%22%3E%3Cpath%20fill%3D%22%236c757d%22%20d%3D%22M8%2015A7%207%200%201%200%208%201a7%207%200%200%200%200%2014m0%201A8%208%200%201%201%208%200a8%208%200%200%201%200%2016%22/%3E%3Cpath%20fill%3D%22%236c757d%22%20d%3D%22M4.5%208a12.5%2012.5%200%200%201%20.527-3.5h5.946A12.5%2012.5%200%200%201%2011.5%208c0%201.23-.181%202.404-.527%203.5H5.027A12.5%2012.5%200%200%201%204.5%208m1.017-4.5a11.5%2011.5%200%200%201%201.004-2.01C7.067.876%207.656.5%208%20.5c.344%200%20.933.376%201.479.99.333.375.679.86%201.004%201.51zM10.483%204.5a10.5%2010.5%200%200%200-.741-1.235C9.284%202.75%208.744%202.5%208%202.5c-.744%200-1.284.25-1.742.765-.229.257-.48.685-.741%201.235zM4.5%208c0%201.23.181%202.404.527%203.5h5.946c.346-1.096.527-2.27.527-3.5s-.181-2.404-.527-3.5H5.027A12.5%2012.5%200%200%200%204.5%208m1.017%204.5a11.5%2011.5%200%200%200%201.004%202.01c.546.614%201.135.99%201.479.99.344%200%20.933-.376%201.479-.99.333-.375.679-.86%201.004-1.51zM10.483%2011.5H5.517c.261.55.512.978.741%201.235.458.515.998.765%201.742.765.744%200%201.284-.25%201.742-.765.229-.257.48-.685.741-1.235M4.964%203.5a13.5%2013.5%200%200%200-.719%202.5h-2.01a7%207%200%200%201%202.729-2.5zm-.719%209a13.5%2013.5%200%200%200%20.719%202.5h-2.73a7%207%200%200%201-.718-2.5zm7.791%202.5a13.5%2013.5%200%200%200%20.719-2.5h2.01a7%207%200%200%201-2.729%202.5zm.719-11.5a13.5%2013.5%200%200%201-.719%202.5h2.01a7%207%200%200%200-2.729-2.5z%22/%3E%3C/svg%3E':
				'website',
		};
		const elements = data.data.value.elements
			.map(element => {
				const resolvedName =
					element.name || (element.src ? legacyIconNameBySrc[element.src] : undefined);
				const websiteIconSize =
					element['icon-size'] ||
					data.attributes['icon-size'] ||
					element['font-size'] ||
					data.attributes['font-size'] ||
					DEFAULT_SOCIAL_ICON_SIZE;
				const websiteIconSrc = getWebsiteIconSrc({
					color: element.color || data.attributes.color || DEFAULT_SOCIAL_COLOR,
					size: websiteIconSize,
				});
				const normalizedElement = {
					...element,
					href: normalizeLinkHref(element.href).replace(/"/g, ''),
					...(resolvedName === 'website' ? { 'icon-size': websiteIconSize } : {}),
					src:
						resolvedName === 'website'
							? websiteIconSrc
							: resolvedName
								? canonicalIconSrcByName[resolvedName] || element.src
								: element.src,
				};
				const elementAttributeStr = Object.keys(normalizedElement)
					.filter(
						key =>
							key !== 'content' &&
							key !== 'name' &&
							normalizedElement[key as keyof typeof normalizedElement] !== '',
					) // filter att=""
					.map(
						key => `${key}="${normalizedElement[key as keyof typeof normalizedElement]}"`,
					)
					.join(' ');
				return `
          <mj-social-element ${elementAttributeStr}>${
						normalizedElement.content ?? ''
					}</mj-social-element>
          `;
			})
			.join('\n');
		return (
			<BasicBlock
				params={params}
				tag='mj-social'
			>
				{elements}
			</BasicBlock>
		);
	},
});
